export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type StoreListener = () => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let listenerRegistered = false;
const listeners = new Set<StoreListener>();

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

export function hasDeferredInstallPrompt(): boolean {
  return deferredPrompt !== null;
}

export function clearDeferredInstallPrompt(): void {
  if (deferredPrompt === null) return;
  deferredPrompt = null;
  notifyListeners();
}

function captureDeferredInstallPrompt(event: BeforeInstallPromptEvent): void {
  deferredPrompt = event;
  notifyListeners();
}

export function subscribeDeferredInstallPrompt(listener: StoreListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Register a single global beforeinstallprompt listener for the app lifetime. */
export function ensureInstallPromptListener(): void {
  if (typeof window === 'undefined' || listenerRegistered) return;

  listenerRegistered = true;
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    captureDeferredInstallPrompt(event as BeforeInstallPromptEvent);
  });
}

export async function promptDeferredInstall(): Promise<boolean> {
  const prompt = deferredPrompt;
  if (!prompt) return false;

  await prompt.prompt();
  const choice = await prompt.userChoice;
  clearDeferredInstallPrompt();
  return choice.outcome === 'accepted';
}

/** Reset module state for unit tests. */
export function resetInstallPromptStoreForTests(): void {
  deferredPrompt = null;
  listenerRegistered = false;
  listeners.clear();
}

/** Test helper to simulate a captured install prompt without a real browser event. */
export function setDeferredInstallPromptForTests(
  prompt: BeforeInstallPromptEvent | null
): void {
  deferredPrompt = prompt;
  notifyListeners();
}
